import { collection, type DocumentData, onSnapshot, type QuerySnapshot } from 'firebase/firestore'
import { createContext, type ReactNode, useEffect, useReducer } from 'react'
import { db } from '@/services/firebase/config'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'
import { useUserData } from '@/store/userData/useUserData'
import type { MealLogEntryDoc } from '@/types/mealLog'
import { initialMealLogState, mealLogReducer } from './mealLogReducer'
import { MealLogActionTypes, type MealLogState } from './types'

export const MealLogContext = createContext<MealLogState | undefined>(undefined)

// The whole cook log in one subscription, so per-meal reads need no index and counts across meals
// stay one read. Writes go through services/firebase/mealLog.
export function MealLogProvider({ children }: { children: ReactNode }) {
  const { uid } = useUserData()
  const [state, dispatch] = useReducer(mealLogReducer, initialMealLogState)

  useEffect(() => {
    if (!uid) dispatch({ type: MealLogActionTypes.CLEAR })
  }, [uid])

  useLiveSubscription<QuerySnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'mealLog',
    subscribe: (activeUid, { onData, onError }) => onSnapshot(collection(db, 'users', activeUid, 'meal_log'), onData, onError),
    onData: (snapshot) => {
      const entries = snapshot.docs.map((d) => {
        const data = d.data({ serverTimestamps: 'estimate' }) as MealLogEntryDoc
        return { ...data, id: d.id, createdAt: data.createdAt.toDate() }
      })
      dispatch({ type: MealLogActionTypes.SET_ENTRIES, payload: entries })
    }
  })

  return <MealLogContext.Provider value={state}>{children}</MealLogContext.Provider>
}
